import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";

const hubLabel = (h) => [h.buildingNo, h.area, h.city].filter(Boolean).join(", ") || h.hubId;

// Register (once, requires images already uploaded - see
// carDeviceRegister/bikeDeviceRegister's "images required" gate) vs. Update
// (once a deviceId exists, hub/odometer.current/fuel are each optional but
// at least one is required - mirrored by disabling Save until something
// actually changed). `hasFuel` is true only for cars - Bike's schema and
// bikeDeviceRegister/bikeDeviceUpdate carry no fuel field at all.
const VehicleDevicePanel = ({ vehicleType, vehicleId, vehicle, hasImages, hasFuel, onSaved }) => {
    const registered = !!vehicle?.deviceId;
    const [hubs, setHubs] = useState([]);
    const [hub, setHub] = useState(vehicle?.hub || "");
    const [deviceId, setDeviceId] = useState("");
    const [odometerInitial, setOdometerInitial] = useState("");
    const [odometerCurrent, setOdometerCurrent] = useState(vehicle?.odometer?.current ?? "");
    const [fuel, setFuel] = useState(vehicle?.fuel?.length ? vehicle.fuel : [{ type: "", percentage: "" }]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "GET", url: "/staff/hub/active" });
            setHubs(response?.success ? response?.data?.data || [] : []);
        })();
    }, []);

    const updateFuelRow = (index, field, value) => {
        setFuel((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };
    const addFuelRow = () => setFuel((prev) => [...prev, { type: "", percentage: "" }]);
    const removeFuelRow = (index) => setFuel((prev) => prev.filter((_, i) => i !== index));

    const buildFuelPayload = () => fuel
        .filter((row) => row.type?.trim() && String(row.percentage).trim() !== "")
        .map((row) => ({ type: row.type.trim(), percentage: Number(row.percentage) }));

    const onRegister = async () => {
        if (!hub) {
            toast.error("Select a hub");
            return;
        }
        if (!deviceId.trim()) {
            toast.error("Device ID is required");
            return;
        }
        if (String(odometerInitial).trim() === "" || Number(odometerInitial) < 0) {
            toast.error("Odometer initial reading is required");
            return;
        }
        setSaving(true);
        const fuelPayload = buildFuelPayload();
        const response = await Handler({
            method: "patch",
            url: `/staff/${vehicleType}/${vehicleId}/device`,
            data: {
                hub,
                deviceId: deviceId.trim(),
                odometer: { initial: Number(odometerInitial) },
                ...(hasFuel && fuelPayload.length ? { fuel: fuelPayload } : {}),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Device registered");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to register device"));
        }
        setSaving(false);
    };

    const onUpdate = async () => {
        const hubChanged = hub && hub !== vehicle?.hub;
        const odometerChanged = String(odometerCurrent).trim() !== "" && Number(odometerCurrent) !== vehicle?.odometer?.current;
        const fuelPayload = buildFuelPayload();
        const fuelChanged = hasFuel && fuelPayload.length > 0;
        if (!hubChanged && !odometerChanged && !fuelChanged) {
            toast.error("Change the hub, odometer reading, or fuel level before saving");
            return;
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: `/staff/${vehicleType}/${vehicleId}/device/update`,
            data: {
                ...(hubChanged ? { hub } : {}),
                ...(odometerChanged ? { odometer: { current: Number(odometerCurrent) } } : {}),
                ...(fuelChanged ? { fuel: fuelPayload } : {}),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Updated successfully");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update device"));
        }
        setSaving(false);
    };

    if (!registered && !hasImages) {
        return <p className="text-muted mb-0">Upload images before registering a device for this {vehicleType}.</p>;
    }

    return (
        <div className="row g-3">
            <div className="col-12 col-md-6">
                <label className="form-label">Hub</label>
                <select className="form-select" value={hub} onChange={(e) => setHub(e.target.value)}>
                    <option value="" disabled>Select hub</option>
                    {hubs.map((h) => (<option key={h.hubId} value={h.hubId}>{hubLabel(h)}</option>))}
                </select>
            </div>

            {!registered ? (
                <>
                    <div className="col-12 col-md-6">
                        <label className="form-label">Device ID</label>
                        <input type="text" className="form-control" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} placeholder="Telematics device ID" />
                    </div>
                    <div className="col-12 col-md-6">
                        <label className="form-label">Odometer (initial reading)</label>
                        <input type="number" className="form-control" value={odometerInitial} onChange={(e) => setOdometerInitial(e.target.value)} min="0" />
                    </div>
                </>
            ) : (
                <div className="col-12 col-md-6">
                    <label className="form-label">Odometer (current reading)</label>
                    <input type="number" className="form-control" value={odometerCurrent} onChange={(e) => setOdometerCurrent(e.target.value)} min="0" />
                </div>
            )}

            {hasFuel ? (
                <div className="col-12">
                    <label className="form-label d-block">Fuel level{!registered ? " (optional)" : ""}</label>
                    {fuel.map((row, index) => (
                        <div key={index} className="d-flex align-items-center gap-2 mb-2">
                            <input type="text" className="form-control" style={{ maxWidth: 160 }} placeholder="Fuel type" value={row.type} onChange={(e) => updateFuelRow(index, "type", e.target.value)} />
                            <input type="number" className="form-control" style={{ maxWidth: 120 }} placeholder="% level" min="0" max="100" value={row.percentage} onChange={(e) => updateFuelRow(index, "percentage", e.target.value)} />
                            {fuel.length > 1 ? (
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeFuelRow(index)}>Remove</button>
                            ) : null}
                        </div>
                    ))}
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addFuelRow}>+ Add fuel type</button>
                </div>
            ) : null}

            <div className="col-12 d-flex justify-content-end">
                <button type="button" className="btn btn-primary btn-sm" onClick={registered ? onUpdate : onRegister} disabled={saving}>
                    {saving ? "Saving..." : registered ? "Save changes" : "Register device"}
                </button>
            </div>
        </div>
    );
};

export default VehicleDevicePanel;
